import { useCallback, useEffect, useState } from 'react'
import cookieStorage from 'cookie-storage'
import { useApolloClient } from 'apollo-client'
import { useSearchParams } from 'router'
import { dataLayer } from 'analytics'
import { constants, formatPrice, GraphQLError, ServerError, UserError } from 'helpers'
import { useCouponData, useResetCouponsCache } from 'modules/coupon'
import type { OriginType } from 'typings/graphql'

import resubscribeQuery, { type ResubscribeVariables } from './graph/resubscribe.graphql'
import resubscribeSessionQuery from './graph/resubscribeSessionInfo.graphql'
import waitForResubscribeQuery, { type WaitForResubscribeVariables, type WaitForResubscribePayload } from './graph/waitForResubscribe.graphql'


export const useResubscribeOrigin = (): OriginType => {
  const isFromEmail = cookieStorage.getItem(constants.cookieNames.fromEmail)

  return isFromEmail ? 'EMAIL' : 'SITE'
}

export const useResubscribeMutation = () => {
  const apolloClient = useApolloClient()

  const resubscribeMutation = useCallback(async (input: ResubscribeVariables['input']) => {
    const result = await apolloClient.mutate({
      mutation: resubscribeQuery,
      variables: {
        input,
      },
      fetchPolicy: 'network-only',
    })

    if (result.errors) {
      throw new GraphQLError(result.errors)
    }

    const { data, error } = result.data.subscriptionResubscribe

    if (error) {
      throw new UserError(error, error.message)
    }

    return data
  }, [ apolloClient ])

  const getResubscribeSessionId = useCallback(async () => {
    const result = await apolloClient.query({
      query: resubscribeSessionQuery,
      fetchPolicy: 'network-only',
    })

    if (result.errors) {
      throw new GraphQLError(result.errors)
    }

    const sessionId = result.data.currentUser?.data?.purchaseSessionInfo?.lastResubscribeSessionIdInProgress

    if (!sessionId) {
      throw new ServerError(null, 'Resubscribe session not found')
    }

    return sessionId
  }, [ apolloClient ])

  const waitForResubscribe = useCallback((input: WaitForResubscribeVariables['input']) => {
    return new Promise<WaitForResubscribePayload['subscriptionResubscribeSession']>((resolve, reject) => {
      const observableQuery = apolloClient.subscribe({
        query: waitForResubscribeQuery,
        fetchPolicy: 'network-only',
        variables: {
          input,
        },
        context: {
          webSocket: true,
        },
      })

      let subscription = observableQuery.subscribe({
        next: ({ data }) => {
          const { session, error } = data.subscriptionResubscribeSession || {}

          if (session?.status === 'SUCCESS') {
            endSubscription()
            resolve(data.subscriptionResubscribeSession)
          }
          else if (session?.status === 'FAILED' || !session && error) {
            endSubscription()
            reject(new UserError(error))
          }
        },
        error: (error) => {
          endSubscription()
          reject(new ServerError(error))
        },
        complete: () => {
          endSubscription()
        },
      })

      const endSubscription = () => {
        if (subscription) {
          subscription.unsubscribe()
          subscription = null
        }
      }
    })
  }, [ apolloClient ])

  return useCallback(async (input: ResubscribeVariables['input']) => {
    try {
      return await resubscribeMutation(input)
    }
    catch (error) {
      const isPostponedPurchase = (
        // if it's a gateway timeout error, then we start waiting for the commit
        error?.networkError?.statusCode === 504
        || error?.cause?.purchaseErrorCode === 'EXTERNAL_NETWORK_ISSUE'
        || error?.cause?.purchaseErrorCode === 'PURCHASE_IN_PROGRESS'
      )

      if (isPostponedPurchase) {
        const sessionId = await getResubscribeSessionId()
        return await waitForResubscribe({ sessionId })
      }

      throw error
    }
  }, [ resubscribeMutation, getResubscribeSessionId, waitForResubscribe ])
}

export const useResubscribe = () => {
  const origin = useResubscribeOrigin()

  const apolloClient = useApolloClient()
  const resubscribeMutation = useResubscribeMutation()
  const resetCouponsCache = useResetCouponsCache()

  return useCallback(async (input?: ResubscribeVariables['input']) => {
    try {
      const data = await resubscribeMutation({
        ...input,
        origin,
      })

      dataLayer.track('resubscribe', {
        resubscribePath: 'from one click',
      })

      return data
    }
    finally {
      // reset queue cache, because plan can change
      apolloClient.cache.evict({ id: 'UserData:{}', fieldName: 'queue' })

      await apolloClient.refetchQueries({
        include: [ 'Subscription' ],
      })

      // ATTN we should reset coupons cache to refetch all coupons info
      resetCouponsCache()
    }
  }, [ apolloClient, origin, resubscribeMutation, resetCouponsCache ])
}

export type ResubscribeOffer = typeof offers[number]
export type EventResubscribeOffer = typeof eventOffers[ResubscribeOffer]

// all available offers
const offers = [
  'discount',
  'off50',
  'off60',
  'freeProduct',
  'freeCase',
  'freeMonth',
  'noDiscount',
] as const

// rotation of offers in regular resubscribe flow
const offersRotation: ResubscribeOffer[] = [
  'discount',
  'off50',
  'freeProduct',
  'freeCase',
  'freeMonth',
  'noDiscount',
]

const eventOffers = {
  'discount': '$6.95 off',
  'off50': '50% off',
  'off60': '60% off',
  'freeProduct': 'Free product',
  'freeCase': 'Free case',
  'freeMonth': 'Free month',
  'noDiscount': 'No offer',
}

const coupons: Partial<Record<ResubscribeOffer, string>> = {
  discount: 'RESUBNEW',
  off50: 'RESUB50',
  off60: 'RESUB60',
  freeProduct: 'RESUBFREEPRODUCT',
  freeCase: 'RESUBFREECASE',
  freeMonth: 'RESUBFREE',
}

export const useResubscribeOffer = (forceOffer?: ResubscribeOffer) => {
  // we can redefine default value by search param
  const [ { forceOffer: queryOffer, giftSubscriptionOffer: queryGiftSubscriptionOffer } ] = useSearchParams<{ forceOffer?: ResubscribeOffer, giftSubscriptionOffer?: string }>()

  const giftSubscriptionOffer = queryGiftSubscriptionOffer && parseInt(queryGiftSubscriptionOffer, 10)

  const [ offer, setOffer ] = useState<ResubscribeOffer>(() => {
    if (forceOffer && offers.includes(forceOffer)) {
      cookieStorage.setSessionItem(constants.cookieNames.resubscribeOffer, forceOffer)
      return forceOffer
    }

    // offer search param has the highest priority, but it should be one of the predefined offers
    if (queryOffer && offers.includes(queryOffer)) {
      return queryOffer
    }

    // then check previously saved offer
    const cookieOffer = cookieStorage.getItem(constants.cookieNames.resubscribeOffer)
    if (cookieOffer && offers.includes(cookieOffer)) {
      return cookieOffer
    }

    // otherwise, first offer from the list
    return offersRotation[0]
  })

  // fetch only non-redeemed coupons
  const couponCode = coupons[offer]
  const offerIndex = offersRotation.indexOf(offer)

  const origin = useResubscribeOrigin()
  const { couponData, isCouponDataFetching, isCouponUnavailable } = useCouponData({
    couponCode,
    origin,
  })

  const needNextOffer = (
    !forceOffer
    && isCouponUnavailable
    && offerIndex >= 0
    && offerIndex < (offers.length - 1)
  )

  const isFetching = isCouponDataFetching || needNextOffer

  useEffect(() => {
    if (needNextOffer) {
      const nextOffer = offers[offerIndex + 1] || 'noDiscount'
      setOffer(nextOffer)
    }
  }, [ offerIndex, needNextOffer ])

  useEffect(() => {
    // save offer to a cookie to keep the same offer across the website
    if (offer && !isFetching) {
      cookieStorage.setSessionItem(constants.cookieNames.resubscribeOffer, offer)
    }
  }, [ offer, isFetching ])

  const isAvailable = !couponCode || Boolean(couponData)
  const freeProduct = couponData?.freeProduct

  const discountAmount = couponData?.discountAmount
  const formattedDiscountAmount = discountAmount ? formatPrice(discountAmount) : null
  const discountPercentage = couponData?.discountPercentage
  const eventOffer: EventResubscribeOffer = offer === 'discount' ? `${formattedDiscountAmount} off` : eventOffers[offer]

  return {
    offer,
    giftSubscriptionOffer,
    eventOffer,
    couponCode,
    freeProduct,
    discountAmount,
    formattedDiscountAmount,
    discountPercentage,
    isFetching,
    isAvailable,
  }
}
